/*jshint esversion: 6 */

import { getServerOverrides } from "../../utils/serverOverride.js";
import { getPlatform } from "../../desktop/platform.js";

// Default URLs - used as fallback in production, overridden by runtime-config.json
const defaultMarketdataUrl = {
  TZ: "https://sr.tradezone.com.br",
  CLEAR: "https://sr.tradezone.com.br",
  XPI: "https://sr.tradezone.com.br",
  RICO: "https://srmdrn.tradezone.com.br",
};

const defaultBrokerUrl = {
  TZ: "https://srsb.tradezone.com.br",
  CLEAR: "https://src.tradezone.com.br",
  XPI: "https://srx.tradezone.com.br",
  RICO: "https://srrn.tradezone.com.br",
};

/*
    Configuration Strategy (Precedence Order):
    - DEVELOPMENT: Vite env vars (.env) → runtime-config.json → defaults
    - PRODUCTION: runtime-config.json → defaults

    Development override: set VITE_MARKETDATA_<BROKER> environment variables
    in your .env to point to local instances (e.g. http://localhost:5001).
    Remember to restart Vite dev server after changing .env.
    
    To test with runtime-config.json in dev, place it in /public/runtime-config.json
    and it will be loaded before applying Vite env var overrides.
*/

export const settings = {
  // Start with defaults - runtime-config.json will override, then Vite vars
  marketdataUrl: { ...defaultMarketdataUrl },
  brokerUrl: { ...defaultBrokerUrl },
  routerVersion: "6.0.0.0",
  marketHours: {
    BMF: 9, // BMF (futures) market opens at 9:00 AM
    BOVESPA: 10, // BOVESPA (stocks) market opens at 10:00 AM
    default: 10, // Default market open hour for unknown exchanges
  },
  // SuperDOM (Livro de Ofertas) fixed price-grid rows rendered on each side
  // of the mid; total ladder height = 2x. Overridable via runtime-config.json.
  bookByPriceRowsPerSide: 80,
  // Themes available on this deploy — names from the registry in
  // src/config/themes.js (unknown names are filtered out with a warn). Arrays
  // are replaced wholesale by applyRuntimeConfig, so a deploy-specific server
  // sets its own list in runtime-config.json (no rebuild):
  //   { "themes": ["lara-light-indigo","lara-dark-indigo","odin-dark"] }
  // Default (dev AND prod) = Light + Dark + Extra Dark; to REMOVE a theme on a
  // deploy, set a shorter list in its runtime-config.json (e.g. the lara pair).
  // `odin-light` (Extra Light) stays in the registry but is disabled for now —
  // re-add it to this list when its navy-light palette is ready.
  // Read at USE time via getAvailableThemes() (src/config/themes.js), never
  // settings.themes directly.
  themes: ["lara-light-indigo", "lara-dark-indigo", "odin-dark"],
  // Feature flags. Each value is `boolean | Platform[]` — read at USE time via
  // isFeatureEnabled(name), never `settings.features.X` directly:
  //   - boolean: on/off on every platform (release gate / kill switch).
  //   - Platform[] (e.g. ["desktop"], ["web","desktop"]): on ONLY for the
  //     current platform (getPlatform()) — platform targeting.
  // Override per environment via runtime-config.json on the server (no rebuild —
  // publish-vw preserves it):
  //   { "features": { "panelPopout": ["web","desktop"], "pointAndFigure": true } }
  // Dev: edit the tracked /public/runtime-config.json locally (don't commit).
  features: {
    // Panel popout to native windows — desktop-only (Nitro.app native windows).
    // Off on web (incl. dev-web): to test popout in a browser, override locally
    // with { "features": { "panelPopout": ["web","desktop"] } }.
    panelPopout: ["desktop"],
    // Release gates (platform-agnostic): ON in dev, OFF in production.
    // Ponto & Figura chart type (#82/#182) — RELEASED na 1.4.5 (paridade bit a
    // bit com o ChartIQ vivo provada em tools/htmlchart-oracle/PROVA-PF.md).
    // Mantida como kill switch via runtime-config.
    pointAndFigure: true,
    // "Motor de cálculo" selector in the Renko pill (#82) — RELEASED na 1.4.5:
    // motor novo (paridade HtmlCharts5) é o default; seletor permite voltar ao
    // anterior. Mantida como kill switch via runtime-config.
    renkoEngineSelector: true,
    // "Meus Desktops" (web bar / native favorites menu — surface picked by platform)
    myDesktops: import.meta.env.DEV === true,
    // Unified chart context menu (right-click / long-press on the chart):
    // always shows the "Gráfico" section (navbar controls) and adds drawing /
    // indicator sections when the click lands on one. RELEASED na 1.4.5 — sempre
    // ligado (kill switch via runtime-config). Independente do device-setting
    // navbarChartControls (esconder controles da navbar é escolha separada).
    // Read at USE time in the ChartRenderer trigger handlers and ChartWrapper mount.
    chartContextMenu: true,
    // Tab "Silverlight" no modal de importação de desktops antigos (listagem
    // via /api/silverlightstorage no marketdata). OFF em prod até o endpoint
    // estar deployado. Read at USE time no LoadLegacyDesktopDialog.
    silverlightImport: import.meta.env.DEV === true,
    // Painel "Info" (dock singleton): mostra os dados da barra sob o cursor +
    // valores de todos os indicadores do gráfico hovered. Contraparte do painel
    // Info do Silverlight. OFF em prod até validar. Read at USE time no botão da
    // Navbar (e no seam de publicação em ChartRenderer.handleInfoHover).
    infoPanel: import.meta.env.DEV === true,
    // Engagement-based rating trigger. Default ON everywhere (dev + prod): this
    // RESTORES a trigger dead since cef0108, so prod-off would make the fix inert.
    // The flag exists as a KILL SWITCH — if the modal over-fires in prod, disable it
    // server-side via runtime-config.json (`"ratingEngagementTrigger": false`) with
    // no rebuild. Read at USE time in ratingStore.registerEngagement.
    ratingEngagementTrigger: true,
    // Cores de alta/baixa das barras (traço e preenchimento) escolhidas pelo
    // usuário, por gráfico — contraparte do "Propriedades da Série" do
    // Silverlight (#112). OFF em prod até validar. Read at USE time em
    // applyBarColors (resolução da paleta) e no modal "Cores das barras".
    // Kill switch: com `false` os pickers somem E a paleta salva é ignorada,
    // voltando às cores padrão sem rebuild.
    barColorCustom: import.meta.env.DEV === true,
    // Linha "Ajuste" (settlement D-1) no painel Info (#437) — depende do
    // marketdata emitir o evento SignalR "RP" (deploy próprio). Kill switch
    // independente do infoPanel: se o backend ainda não tiver o RP, o painel
    // funciona sem a linha. Read at USE time no InfoPanel.
    infoAjuste: import.meta.env.DEV === true,
    // Linhas "Fase" e "Teórico" (eventos "SS"/"TP", marketdata#25) — Teórico
    // só aparece com fase em leilão. Kill switch independente; sem o backend
    // novo o painel funciona sem as linhas. Read at USE time no InfoPanel.
    infoFase: import.meta.env.DEV === true,
    // Linha "Em Aberto" (contratos em aberto, evento "OI", marketdata#25).
    infoOpenInterest: import.meta.env.DEV === true,
  },
  gapAutoReload: false, // Auto-reload chart data on trade gap detection
  // Legacy chart visibility settings
  EnableLegacyChart: false, // Default: hide legacy chart option
  ForceLegacyChart: [], // Users that always see legacy chart option: [{broker: "XPI", login: "user1"}, ...]
};

// Apply runtime overrides (object shape: { marketdataUrl: { XPI: 'https://...', ... }, brokerUrl: { XPI: 'https://...' } })
// Apply runtime overrides from runtime-config.json
// Behavior:
// - For object values: performs shallow merge into existing settings (per-key replace)
// - For primitive values: replaces the existing value
export const applyRuntimeConfig = (cfg) => {
  if (!cfg || typeof cfg !== "object") return;

  for (const key of Object.keys(cfg)) {
    const val = cfg[key];
    if (val == null) continue;

    // If the existing setting is an object and the incoming value is an object,
    // perform a per-key shallow merge (replace keys that exist in incoming value)
    if (
      typeof settings[key] === "object" &&
      !Array.isArray(settings[key]) &&
      typeof val === "object" &&
      !Array.isArray(val)
    ) {
      for (const k of Object.keys(val)) {
        if (val[k] != null) settings[key][k] = val[k];
      }
    } else {
      // Primitive or non-object: replace directly
      settings[key] = val;
    }
  }
};

/**
 * Resolve a feature flag at USE time (render/callback), honoring both shapes:
 *   - boolean   → on/off on every platform.
 *   - Platform[] → on only when it targets the current platform (getPlatform()).
 * runtime-config.json can override either shape (the merge is value-agnostic),
 * so a kill switch is just setting the flag to `false` (or `[]`).
 * @param {string} name key under settings.features
 * @returns {boolean}
 */
export const isFeatureEnabled = (name) => {
  const value = settings.features?.[name];
  return Array.isArray(value) ? value.includes(getPlatform()) : value === true;
};

/**
 * Parse URL string into array of servers for load balancing
 * Supports multiple URLs separated by semicolon (;)
 *
 * Examples:
 *   "https://server1.com" → ["https://server1.com"]
 *   "https://localhost:5001" → ["https://localhost:5001"] (preserves port)
 *   "https://server1.com;https://server2.com" → ["https://server1.com", "https://server2.com"]
 *   "https://server1.com:8080;https://server2.com:9090" → ["https://server1.com:8080", "https://server2.com:9090"]
 *
 * @param {string} urlString - Single URL or semicolon-separated multi-URL string
 * @returns {string[]} - Array of URLs (all equal priority)
 */
export const parseServerUrls = (urlString) => {
  if (!urlString || typeof urlString !== "string") return [];

  // Split by semicolon only (to preserve port numbers like :5001)
  // Example: "https://server1.com:5001;https://server2.com:8080" → ["https://server1.com:5001", "https://server2.com:8080"]
  const urls = urlString
    .split(";")
    .map((url) => url.trim())
    .filter((url) => url.length > 0);

  return urls.length > 0 ? urls : [urlString];
};

// Storage for parsed URLs (populated after config is loaded)
// Format: { "TZ": ["url1", "url2"], "XPI": ["url1"], ... }
export const parsedMarketdataUrls = {};
export const parsedBrokerUrls = {};

/**
 * Parse all configured URLs into arrays for load balancing
 * Call this after applyRuntimeConfig and applyViteEnvOverrides
 */
export const parseAllUrls = () => {
  // Parse marketdata URLs
  for (const broker of Object.keys(settings.marketdataUrl)) {
    parsedMarketdataUrls[broker] = parseServerUrls(
      settings.marketdataUrl[broker],
    );
  }

  // Parse broker URLs
  for (const broker of Object.keys(settings.brokerUrl)) {
    parsedBrokerUrls[broker] = parseServerUrls(settings.brokerUrl[broker]);
  }

  console.log("[Settings] Parsed marketdata URLs:", parsedMarketdataUrls);
  console.log("[Settings] Parsed broker URLs:", parsedBrokerUrls);
};

// Apply Vite environment variable overrides (highest priority in development)
export const applyViteEnvOverrides = () => {
  if (import.meta.env.MODE !== "development") return;

  // Override marketdata URLs from Vite env vars
  if (import.meta.env.VITE_MARKETDATA_TZ)
    settings.marketdataUrl.TZ = import.meta.env.VITE_MARKETDATA_TZ;
  if (import.meta.env.VITE_MARKETDATA_CLEAR)
    settings.marketdataUrl.CLEAR = import.meta.env.VITE_MARKETDATA_CLEAR;
  if (import.meta.env.VITE_MARKETDATA_XPI)
    settings.marketdataUrl.XPI = import.meta.env.VITE_MARKETDATA_XPI;
  if (import.meta.env.VITE_MARKETDATA_RICO)
    settings.marketdataUrl.RICO = import.meta.env.VITE_MARKETDATA_RICO;

  // Override broker URLs from Vite env vars
  if (import.meta.env.VITE_ROUTER_TZ)
    settings.brokerUrl.TZ = import.meta.env.VITE_ROUTER_TZ;
  if (import.meta.env.VITE_ROUTER_CLEAR)
    settings.brokerUrl.CLEAR = import.meta.env.VITE_ROUTER_CLEAR;
  if (import.meta.env.VITE_ROUTER_XPI)
    settings.brokerUrl.XPI = import.meta.env.VITE_ROUTER_XPI;
  if (import.meta.env.VITE_ROUTER_RICO)
    settings.brokerUrl.RICO = import.meta.env.VITE_ROUTER_RICO;
};

/**
 * Apply URL parameter overrides for debugging (highest priority)
 * Usage: ?mkd_srv=debug1&rtr_srv=debug1
 * This overrides ALL brokers to point to the specified subdomain
 */
export const applyUrlParamOverrides = () => {
  const overrides = getServerOverrides();

  if (overrides.marketDataUrl) {
    // Override all brokers to use the debug server
    for (const broker of Object.keys(settings.marketdataUrl)) {
      settings.marketdataUrl[broker] = overrides.marketDataUrl;
    }
    console.warn(
      `[Settings] All MarketData URLs overridden to: ${overrides.marketDataUrl}`,
    );
  }

  if (overrides.routerUrl) {
    // Override all brokers to use the debug server
    for (const broker of Object.keys(settings.brokerUrl)) {
      settings.brokerUrl[broker] = overrides.routerUrl;
    }
    console.warn(
      `[Settings] All Router URLs overridden to: ${overrides.routerUrl}`,
    );
  }
};
